/* eslint-disable no-console */
import { Navigate, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { ChevronUp, XClose } from '@untitled-ui/icons-react';
import { useEffect, useRef, useState } from 'react';

import { ContentHeader } from '../../../components/content-header';
import { setCustomMetaTitle } from '../../../components/meta-title';
import { RatingDialog } from '../../../components/rating/rating-dialog';
import { ToastVariant } from '../../../components/toast';
import { ContentStatus } from '../../../entity/api/content';
import { PlanError, PlanFeature } from '../../../entity/api/plan';
import { BadRequestError, ForbiddenError, NotFoundError, UnauthorizedError } from '../../../error';
import { convertError } from '../../../error/convert';
import { addTrackCrumb, trackError } from '../../../error/tracking';
import { captureException } from '../../../logger/capture-exception';
import { getQueryClient } from '../../../sdk/request';
import { PostHogEvents, capturePostHogEvent } from '../../../services/posthog';
import {
  fetchArticleByIdImperative,
  getOrganizationBillingFromCache,
  useArticleById,
  useUpdateArticle,
} from '../../../services/storage';
import { useArticleAnalysisByArticleId } from '../../../services/storage/articles-analysis-queries';
import { useSaveArticleUserRating } from '../../../services/storage/articles-mutations';
import { useBrandById } from '../../../services/storage/brands-queries';
import { useOrganizationBilling } from '../../../services/storage/organization-billing-queries';
import { useOrganizationById } from '../../../services/storage/organizations-queries';
import { getAuthUserFromCache } from '../../../services/storage/users-queries';
import { tNamespace } from '../../../services/translation';
import { isArticleEmpty } from '../../../shared/content';
import { useToast } from '../../../shared/contexts/toast-context';
import { usePreventLeave } from '../../../shared/hooks/use-prevent-leave';
import { isMobile } from '../../../shared/mobile';
import { canOrgChangeArticles, getSelectedOrganization } from '../../../shared/organization';
import { hasOrgFeature, isOrgCancelled } from '../../../shared/subscription';
import { useContentModals } from '../_content-modals-context';
import { ContentEditor } from './-editor/content-editor';
import { ContentEditorLoader } from './-editor/content-editor-loader';
import { EditorSidebar } from './-editor/editor-sidebar';
import { EditorToolbar } from './-editor/editor-toolbar';
import { ModalCancelledSub } from './-editor/modal-cancelled-sub';
import { SidebarLoader } from './-editor/sidebar-loader';

const PARENT_ROUTE = '/app/blogs';

export const t = tNamespace('app.content.$id');
const tErrors = tNamespace('errors');

export const Route = createFileRoute('/app/content/$id')({
  beforeLoad: function ({ params }) {
    const { id } = params;
    addTrackCrumb({
      category: 'route',
      message: '/app/content/$id',
      level: 'info',
    });
    addTrackCrumb({
      category: 'editor',
      message: `entering editor with content ${id}`,
      level: 'info',
    });

    const queryClient = getQueryClient();
    const currentOrg = getSelectedOrganization(getAuthUserFromCache(queryClient));
    const orgBilling = getOrganizationBillingFromCache(queryClient, currentOrg?._id ?? '');

    // Allow access in teasing/read-only mode when the user has a free article created
    // even if they have no active plan or the EnabledArticles feature.
    const hasFreeArticleAccess = orgBilling?.hasFreeArticleCreated === true;

    // we require an organization with the right permissions, send to the parent if not
    if (
      isOrgCancelled(orgBilling) ||
      (!hasOrgFeature(orgBilling, PlanFeature.EnabledArticles) && !hasFreeArticleAccess)
    ) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: PARENT_ROUTE,
      });
    }

    fetchArticleByIdImperative(queryClient, id).catch(function (err) {
      captureException(
        err,
        {
          file: 'content/$id.tsx',
          method: 'loader',
          infoMessage: 'unable to get content by id',
          id,
        },
        trackError
      );

      if (
        err instanceof BadRequestError ||
        err instanceof NotFoundError ||
        err instanceof UnauthorizedError
      ) {
        // we require an article to be able to proceed, send to the parent
        window.location.href = '/app/blogs';
        return;
      }

      throw err;
    });
  },
  // eslint-disable-next-line no-use-before-define
  component: Index,
});

export type SaveState = 'error' | 'saving' | 'saved' | 'default';

function ContentLoading() {
  return (
    <>
      <div className="relative grid h-screen flex-1 place-content-center">
        <div className="mt-16">
          {/* NOTE: EditorToolbar is only decorative here, hence the empty functions */}
          <EditorToolbar
            canRedo={false}
            canUndo={false}
            onToggleBold={() => {}}
            onToggleItalic={() => {}}
            onToggleUnderline={() => {}}
            onToggleStrikethrough={() => {}}
            onToggleBulletList={() => {}}
            onToggleOrderedList={() => {}}
            onToggleLink={() => {}}
            onSelectHeading1={() => {}}
            onSelectHeading2={() => {}}
            onSelectHeading3={() => {}}
            onSelectHeading4={() => {}}
            onSelectParagraph={() => {}}
            onSelectAlignLeft={() => {}}
            onSelectAlignRight={() => {}}
            onSelectAlignCenter={() => {}}
            onSelectJustify={() => {}}
            onToggleCode={() => {}}
            onSelectHorizontalRule={() => {}}
            onCreateTable={() => {}}
            onAddImage={() => {}}
            onUndo={() => {}}
            onRedo={() => {}}
            onSelectCodeBlock={() => {}}
            onSelectBlockquote={() => {}}
            isBold={false}
            isItalic={false}
            isUnderline={false}
            isStrikethrough={false}
            isBulletList={false}
            isOrderedList={false}
            isLink={false}
            isHTMLMode={false}
          />
        </div>
        <ContentEditorLoader />
      </div>
      <div className="hidden h-full min-w-[352px] max-w-[432px] overflow-hidden border-l border-branding-gray p-3 pb-0 pr-0 md:block">
        <SidebarLoader />
      </div>
    </>
  );
}

function Index() {
  const { id } = Route.useParams();

  // TanStack Query hooks for article and funnel data
  const articleQuery = useArticleById(id);
  const article = articleQuery.data;
  const analysisQuery = useArticleAnalysisByArticleId(id);

  // TanStack Query for organization, billing, and brand
  const { data: activeBrand } = useBrandById(article?.brandId);
  const { data: organization } = useOrganizationById(article?.organizationId);
  const { data: orgBillingStatus } = useOrganizationBilling(organization?._id);

  // TanStack Query mutations
  const updateArticleMutation = useUpdateArticle();

  const addToast = useToast();
  const { openTrialLimitModal } = useContentModals();
  const headerRef = useRef<HTMLHeadElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [toolbarSavingState, setToolbarSavingState] = useState<SaveState>('default');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isModalCancelledSubOpen, setIsModalCancelledSubOpen] = useState(false);
  const [isRatingDialogOpen, setIsRatingDialogOpen] = useState(false);
  const [isSidebarDrawerOpen, setIsSidebarDrawerOpen] = useState(false);
  const IS_TEASING_EDITOR = !canOrgChangeArticles(orgBillingStatus ?? null);
  const isMobileDevice = isMobile();
  const { mutateAsync: saveArticleRating } = useSaveArticleUserRating();

  usePreventLeave(hasUnsavedChanges);

  const navigate = useNavigate({ from: '/app/content/$id' });

  async function saveContent(args: {
    content: Record<string, any>;
    metaTitle: string | null;
    metaDescription: string | null;
    urlSlug: string | null;
  }): Promise<(() => void) | null> {
    const { content, metaTitle, metaDescription, urlSlug } = args;
    if (IS_TEASING_EDITOR) {
      return null;
    }
    if (article == null) {
      setToolbarSavingState('default');
      return null;
    }

    try {
      setToolbarSavingState('saving');
      const body = JSON.stringify(content);
      await updateArticleMutation.mutateAsync({
        id: article._id,
        data: {
          ...article,
          metaTitle: metaTitle ?? article.metaTitle,
          metaDescription: metaDescription ?? article.metaDescription,
          urlSlug: urlSlug ?? article.urlSlug,
          body,
        },
      });

      setHasUnsavedChanges(false);

      let timerB: ReturnType<typeof setTimeout> | null;
      let timerA: ReturnType<typeof setTimeout> | null;

      timerA = setTimeout(function () {
        setToolbarSavingState('saved');
        timerB = setTimeout(function () {
          setToolbarSavingState('default');
        }, 1000);
      }, 2000);

      return function () {
        if (timerA != null) {
          clearTimeout(timerA);
          timerA = null;
        }

        if (timerB != null) {
          clearTimeout(timerB);
          timerB = null;
        }
      };
    } catch (err) {
      const handledErr = convertError(err);
      if (handledErr instanceof ForbiddenError) {
        if ((handledErr.originalMessage as PlanError) === PlanError.LimitArticleTrialing) {
          openTrialLimitModal();
          setToolbarSavingState('default');
          return null;
        }
        addToast({
          variant: ToastVariant.Error,
          title: tErrors(handledErr.originalMessage),
          id: Date.now(),
        });
        setToolbarSavingState('default');
        return null;
      }

      captureException(
        err,
        {
          file: 'content/$id.tsx',
          method: 'saveContent',
          infoMessage: 'unable to saveContent',
          id,
        },
        trackError
      );
      setToolbarSavingState('error');
      return null;
    }
  }

  async function onArticleUserRatingUpdate(args: { newRating: number }) {
    if (id == null || id.length === 0) {
      return;
    }

    try {
      await saveArticleRating({
        id,
        userRating: {
          general: args.newRating,
        },
      });

      capturePostHogEvent(PostHogEvents.CONTENT_GRADED, {
        contentId: id,
        grade: args.newRating,
        seoScore: article?.seoScore,
      });

      setIsRatingDialogOpen(true);
    } catch (err) {
      captureException(
        err,
        {
          file: 'content/$id.tsx',
          method: 'onArticleUserRatingUpdate',
          infoMessage: 'unable to onArticleUserRatingUpdate',
          id,
        },
        trackError
      );

      return null;
    }
  }

  async function onArticleRatingDialogSubmit(data?: {
    infoQuality: number;
    structureQuality: number;
    toneVoice: number;
    comments?: string;
  }) {
    if (id == null || id.length === 0) {
      return;
    }

    try {
      if (data != null) {
        await saveArticleRating({
          id,
          userRating: {
            general: article?.userRating?.general ?? 1,
            infoQuality: data.infoQuality,
            structureQuality: data.structureQuality,
            toneVoice: data.toneVoice,
            comments: data.comments,
          },
        });
      }

      setIsRatingDialogOpen(false);

      addToast({
        title: t('ratingToastTitle'),
        description: t('ratingToastDescription'),
        variant: ToastVariant.Success,
        id: Date.now(),
        duration: 5000,
        className: 'min-w-[500px]',
        hideCrossClose: true,
        cta: {
          text: t('undo'),
          closeOnClick: true,
          onClick: function () {
            saveArticleRating({
              id,
              userRating: undefined,
            }).catch(function (err) {
              captureException(
                err,
                {
                  file: 'content/$id.tsx',
                  method: 'saveArticleRating',
                  infoMessage: 'unable to save article rating undo',
                  id,
                },
                trackError
              );
            });
          },
        },
      });
    } catch (err) {
      captureException(
        err,
        {
          file: 'content/$id.tsx',
          method: 'handleRatingDialogSubmit',
          infoMessage: 'unable to submit rating dialog',
          id,
        },
        trackError
      );
    }
  }

  async function saveKeywords(args: { primaryKeyword: string; secondaryKeywords: string[] }) {
    if (article == null) {
      setToolbarSavingState('default');
      return null;
    }
    if (IS_TEASING_EDITOR) {
      return null;
    }

    // TODO: are these keywords as expected?
    await updateArticleMutation.mutateAsync({
      id: article._id,
      data: {
        ...article,
        secondaryKeywords: args.secondaryKeywords,
      },
    });
  }

  // async function startAiFunnel() {
  //   if (organization == null) {
  //     return;
  //   }
  //   if (IS_TEASING_EDITOR && article?.wordCount !== 0) {
  //     return;
  //   }

  //   try {
  //     await createFunnelMutation.mutateAsync(id);
  //     await navigate({
  //       to: `/app/funnel/${id}/customize-content`,
  //     });
  //   } catch (err) {
  //     addToast({
  //       title: tGlobal('somethingWentWrong'),
  //       variant: ToastVariant.Error,
  //       id: Date.now(),
  //     });
  //     captureException(
  //       err,
  //       {
  //         file: 'app/content/-editor/dialog-funnel-first-step.tsx',
  //         method: 'startAiFunnel',
  //         infoMessage: 'unable to start ai funnel',
  //       },
  //       trackError
  //     );
  //   } finally {
  //     setIsDialogStartAiFunnelOpen(false);
  //   }
  // }

  useEffect(
    function () {
      if (headerRef?.current?.clientHeight != null) {
        setHeaderHeight(headerRef.current.clientHeight);
      }
    },
    [headerRef]
  );

  useEffect(function () {
    document?.body.classList.add('overflow-hidden');

    return function () {
      document?.body.classList.remove('overflow-hidden');
    };
  }, []);

  // useEffect(
  //   function () {
  //     if (articleFunnel == null && article?.wordCount === 0) {
  //       setIsDialogStartAiFunnelOpen(true);
  //     }
  //   },
  //   [articleFunnel, article]
  // );

  useEffect(
    function () {
      if (organization != null && orgBillingStatus != null && isOrgCancelled(orgBillingStatus)) {
        window.location.href = '/app/blogs';
      }
    },
    [organization, orgBillingStatus]
  );

  useEffect(
    function () {
      if (articleQuery.isLoading) {
        return;
      }
      if (article?.status === ContentStatus.Analysing || isArticleEmpty(article)) {
        void navigate({ to: PARENT_ROUTE });
      }
    },
    [article, article?.status, articleQuery.isLoading, navigate]
  );

  useEffect(
    function () {
      if (article != null) {
        setCustomMetaTitle(article.primaryKeyword);
      }

      if (articleQuery.error == null) {
        return;
      }

      captureException(
        articleQuery.error,
        {
          file: 'content/$id.tsx',
          method: 'Index',
          infoMessage: 'unable to fetch the article',
          id,
        },
        trackError
      );
      // navigate to the app
      navigate({ to: PARENT_ROUTE }).catch(function (err) {
        captureException(
          err,
          {
            file: 'content/$id.tsx',
            method: 'Index',
            infoMessage: 'unable to navigate to /app',
            id,
          },
          trackError
        );
      });
    },
    [article, articleQuery.error, id, navigate]
  );

  return (
    <>
      <ContentHeader
        onSaveKeywords={saveKeywords}
        brand={activeBrand}
        ref={headerRef}
        content={article}
        isExportDisabled={IS_TEASING_EDITOR || isMobileDevice}
        isPrimaryKeywordDisabled={IS_TEASING_EDITOR}
        onTrialLimitError={openTrialLimitModal}
        onClose={async function () {
          if (hasUnsavedChanges) {
            // TODO: this can be controlled, maybe needs design?
            const confirm = window.confirm(t('unsavedChanges'));
            if (!confirm) {
              return;
            }
          }
          await navigate({
            to: PARENT_ROUTE,
          }).catch(function (err) {
            captureException(
              err,
              {
                file: 'content/$id.tsx',
                method: 'Index',
                infoMessage: 'unable to navigate to /app',
                id,
              },
              trackError
            );
          });
        }}
        savingState={toolbarSavingState}
      />
      <main
        className="relative flex w-full md:min-w-[800px]"
        style={{ height: `calc(100% - ${headerHeight}px)` }}
      >
        {articleQuery.error != null ? <Navigate to="/app" /> : null}
        {articleQuery.error == null && !articleQuery.isLoading ? (
          <>
            <div className="h-full w-full flex-1 overflow-hidden">
              <ContentEditor
                setHasUnsavedChanges={setHasUnsavedChanges}
                isToolbarDisabled={IS_TEASING_EDITOR}
                isEditorDisabled={IS_TEASING_EDITOR}
                isMetaDivIsDisabled={IS_TEASING_EDITOR}
                onArticleUserRatingUpdate={onArticleUserRatingUpdate}
                onContentUpdate={async function (args) {
                  await saveContent(args);
                }}
                contentQuery={article}
                brandWebsite={activeBrand?.companyWebsite}
              />
            </div>

            {/* Desktop Sidebar */}
            <div className="hidden h-full min-w-[352px] max-w-[432px] overflow-hidden border-l border-branding-gray md:block">
              <EditorSidebar
                contentId={id}
                isUpdatingTermsDisabled={IS_TEASING_EDITOR}
                articleQuery={articleQuery}
                analysisQuery={analysisQuery}
              />
            </div>

            {/* Mobile Bottom Drawer Backdrop */}
            {isSidebarDrawerOpen ? (
              <div
                className="fixed inset-0 z-40 bg-black/30 transition-opacity duration-300 md:hidden"
                onClick={function () {
                  setIsSidebarDrawerOpen(false);
                }}
              />
            ) : null}

            {/* Mobile Bottom Drawer */}
            <div
              className="fixed bottom-0 left-0 right-0 z-50 shadow-lg transition-transform duration-300 ease-in-out md:hidden"
              style={{
                transform: isSidebarDrawerOpen ? 'translateY(0)' : 'translateY(calc(100% - 70px))',
                maxHeight: '85vh',
              }}
            >
              <button
                onClick={function () {
                  setIsSidebarDrawerOpen(!isSidebarDrawerOpen);
                }}
                className="relative w-full transition-colors focus:outline-none"
              >
                <EditorSidebar
                  contentId={id}
                  isUpdatingTermsDisabled={IS_TEASING_EDITOR}
                  showOnlyProgressBar={true}
                  articleQuery={articleQuery}
                  analysisQuery={analysisQuery}
                />
                <div className="absolute right-4 top-1/3 -translate-y-1/2">
                  {isSidebarDrawerOpen ? (
                    <XClose width={24} height={24} />
                  ) : (
                    <ChevronUp width={16} height={16} />
                  )}
                </div>
              </button>
              <div
                className="overflow-y-auto border-none md:border-t md:border-branding-gray"
                style={{ maxHeight: 'calc(85vh - 120px)' }}
              >
                <EditorSidebar
                  contentId={id}
                  isUpdatingTermsDisabled={IS_TEASING_EDITOR}
                  hideProgressBar={true}
                  articleQuery={articleQuery}
                  analysisQuery={analysisQuery}
                />
              </div>
            </div>
          </>
        ) : null}
        {articleQuery.error == null && articleQuery.isLoading ? <ContentLoading /> : null}
      </main>
      {/* <DialogFunnelFirstStep
        isOpen={isDialogStartAiFunnelOpen}
        setIsOpen={function (open) {
          if (IS_TEASING_EDITOR) {
            navigate({ to: PARENT_ROUTE }).catch(function (err) {
              captureException(
                err,
                {
                  file: 'content/$id.tsx',
                  method: 'Index',
                  infoMessage: 'unable to navigate to /app',
                  id,
                },
                trackError
              );
            });
            return;
          }
          setIsDialogStartAiFunnelOpen(open);
        }}
        startAiFunnel={function () {
          void startAiFunnel();
        }}
        isFunnelLoading={createFunnelMutation.isPending}
        isManualStartDisabled={IS_TEASING_EDITOR}
      /> */}
      <ModalCancelledSub
        isOpen={isModalCancelledSubOpen}
        onOpenChange={setIsModalCancelledSubOpen}
        backButton={function () {
          void navigate({ to: PARENT_ROUTE });
        }}
        reactivateSubscription={function () {
          const link = `/app/settings/organization/${organization?._id}/plan`;
          void navigate({ to: link });
        }}
      />
      <RatingDialog
        open={isRatingDialogOpen}
        onClose={(data) => void onArticleRatingDialogSubmit(data)}
      />
    </>
  );
}
